import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchMemberProfile } from "../services/apiService";
import { invalidateCache } from "../services/apiService";
import Loader from "../components/Loader";

// Function to simulate getting a token (Replace with actual Auth context/storage)
const getAccessToken = () => {
  // Example: Replace with secure method of retrieving token
  const token = localStorage.getItem('accessToken'); 
  return token; 
};

export default function EditMember({ isDarkMode, toggleDarkMode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    rollNumber: "",
    email: "",
    class: "", 
    year: "",
    profileImage: "", // Holds the image URL or Base64 string for display
  });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [imageFile, setImageFile] = useState(null); // 💡 NEW: State to hold the actual File object
  const [imagePreview, setImagePreview] = useState("");
  const [classDropdownOpen, setClassDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(""); // NEW STATE FOR TOAST
  const [isSubmitting, setIsSubmitting] = useState(false); // Loader state for form actions
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Load member data (GET API with caching)
  useEffect(() => {
    if (id) {
      const loadMemberData = async () => {
        setIsLoadingData(true);
        const token = getAccessToken();
        if (!token) {
          alert("Authentication required. Please log in as an Admin.");
          navigate("/login"); 
          return;
        }
        
        try {
          // Use cached API service
          const result = await fetchMemberProfile(id);
          
          if (result.data && result.data.user) {
            const userData = result.data.user;
            setFormData({
              name: userData.fullname || "", 
              rollNumber: userData.roll_no || "", 
              email: userData.email || "",
              class: userData.classes || "", 
              year: userData.year || "",
              profileImage: userData.avatar || "", 
            });
            setSelectedTeam(userData.team ? userData.team.charAt(0).toUpperCase() + userData.team.slice(1) : null);
            setImagePreview(userData.avatar || "");
          } else {
            alert("Member data is empty!");
            navigate("/dashboard");
          }
        } catch (error) {
          console.error("Error fetching member:", error);
          if (error.message.includes('404')) {
            alert(`Member not found!`);
          } else {
            alert(`An error occurred while fetching member data: ${error.message}`);
          }
          navigate("/dashboard");
        } finally {
          setIsLoadingData(false);
        }
      };
      loadMemberData();
    }
  }, [id, navigate]);

  // Close dropdowns when clicking outside (unchanged)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classDropdownOpen && !event.target.closest('[data-class-dropdown]')) {
        setClassDropdownOpen(false);
      }
      if (yearDropdownOpen && !event.target.closest('[data-year-dropdown]')) {
        setYearDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [classDropdownOpen, yearDropdownOpen]);

  const classes = [
    { value: "CSE1", label: "CSE1", icon: "fa-book" },
    { value: "CSE2", label: "CSE2", icon: "fa-book" },
    { value: "CSE3", label: "CSE3", icon: "fa-book" },
    { value: "CSE4", label: "CSE4", icon: "fa-book" },
    { value: "AIML", label: "AIML", icon: "fa-robot" }
  ];
  const years = [
    { value: "1st Year", label: "1st Year", icon: "fa-graduation-cap", color: "#667eea" },
    { value: "2nd Year", label: "2nd Year", icon: "fa-graduation-cap", color: "#764ba2" },
    { value: "3rd Year", label: "3rd Year", icon: "fa-graduation-cap", color: "#f093fb" },
    { value: "4th Year", label: "4th Year", icon: "fa-graduation-cap", color: "#4facfe" }
  ];
  const teams = ["Management", "Tech", "Design", "PR-Content", "Media", "Support"];

  const getTeamColor = (team) => {
    const colors = {
      Management: { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 0.5)', color: '#fbbf24' },
      Tech: { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.5)', color: '#3b82f6' },
      Design: { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 0.5)', color: '#a855f7' },
  'PR-Content': { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.5)', color: '#22c55e' },
      Media: { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.5)', color: '#ef4444' },
      Support: { bg: 'rgba(14, 165, 233, 0.2)', border: 'rgba(14, 165, 233, 0.5)', color: '#0ea5e9' }
    };
    return colors[team] || colors.Tech;
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        setImagePreview(result);
        setFormData({ ...formData, profileImage: result });
      };
      reader.readAsDataURL(file);
      setImageFile(file); // 💡 Capture the actual file object
    } else {
      setImageFile(null); // Clear the file if the user clears the selection
    }
  };

  const handleSubmit = async (e) => { // ⬅️ Made async
    e.preventDefault();
    // 1. Validation Checks
    if (!selectedTeam) {
      alert("Please select a team");
      return;
    }
    if (!formData.name || !formData.rollNumber || !formData.email || !formData.class || !formData.year) {
      alert("Please fill all required fields (Name, Roll No, Email, Class, Year)");
      return;
    }
    // 2. Prepare Data and Auth
    const token = getAccessToken();
    if (!token) {
        alert("Authentication failed. Please log in again.");
        navigate("/login");
        return;
    }
    // 3. Construct FormData Payload
    // Because the request requires file upload (avatar), we must use FormData
    const updateFormData = new FormData();
    // Map local state keys to expected API payload keys (from the image)
    updateFormData.append('fullname', formData.name);
    updateFormData.append('email', formData.email);
    updateFormData.append('classes', formData.class);
    updateFormData.append('roll_no', formData.rollNumber);
    updateFormData.append('year', formData.year);
    // API expects team in lowercase, e.g., 'management'
    updateFormData.append('team', selectedTeam.toLowerCase()); 
    // Append the avatar file IF a new file was selected
    if (imageFile) {
        // This sends the actual image file object
        updateFormData.append('avatar', imageFile); 
    } else if (imagePreview) {
        // If the preview is still there (meaning no change, or cleared), 
        // we might need to send the existing URL if the API is built to handle it.
        // If the API only handles new files, this 'else if' block should be omitted.
        // For robustness, we will assume we only send the file if it was explicitly changed (imageFile is not null).
    }
    // 4. API Call
    // 🔗 Endpoint: PATCH https://taskscheduler-backend.onrender.com/api/v1/auth/user/:userId
    const apiUrl = `https://taskscheduler-backend.onrender.com/api/v1/auth/user/${id}`;
    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}` // Admin token required
        },
        body: updateFormData // Send the FormData object
      });
      const result = await response.json();
      if (!response.ok) {
        console.error("Update API Error:", result);
        alert(`Update failed: ${result.message || 'Server error occurred.'}`);
        if (response.status === 403 || response.status === 401) {
            navigate("/dashboard"); 
        }
        return;
      }
      console.log('✅ Member updated successfully, invalidating cache');
      invalidateCache.members();
      invalidateCache.memberProfile(id);
      
      setToastMessage("Member updated successfully! 🎉");
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (error) {
      console.error("Network or Unexpected Error during update:", error);
      alert("An unexpected error occurred during the update process.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const themeBg = isDarkMode 
    ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    : '#ffffff';
  const themeText = isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#2c3e50';
  const themeCardBg = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)';
  const themeBorder = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)';
  const themeInputBg = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 1)';
  const themeInputBorder = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)';

  if (isLoadingData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: themeBg,
        padding: '2rem'
      }}>
        <Loader variant="page" text="Loading member details..." isDarkMode={isDarkMode} size="lg" />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0, 
      background: themeBg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      position: 'relative',
      width: '100%'
    }}>
      {/* ✅ ADD THIS TOAST DIV HERE */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          top: '20px', // Position from top
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#d1fae5', // Light green background (from your image)
          color: '#059669', // Dark green text (from your image)
          border: '1px solid #a7f3d0', // Lighter green border
          borderRadius: '0.5rem', // Rounded corners
          padding: '0.75rem 1.5rem', // Padding inside
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', // Subtle shadow
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem', // Space between icon and text
          zIndex: 1000, // Ensure it's on top
          fontSize: '0.9375rem', // Font size
          fontWeight: '500', // Font weight
          animation: 'fadeInOut 1.5s ease-out' // Optional: Fade in/out animation
        }}>
          <i className="fas fa-check-circle" style={{ fontSize: '1rem' }}></i> {/* Green check icon */}
          {toastMessage}
        </div>
      )}

      <div className="edit-member-form" style={{
        width: '100%',
        maxWidth: window.innerWidth <= 768 ? '100%' : '42rem', 
        animation: 'pageEnter 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div 
          className={isDarkMode ? 'glass-card-dark' : 'glass-card'}
          style={{
            borderRadius: window.innerWidth <= 768 ? '1rem' : '1.5rem',
            padding: window.innerWidth <= 768 ? '1.5rem' : '2rem',
            border: `1px solid ${themeBorder}`,
            position: 'relative'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            marginBottom: '2rem'
          }}>
            <h1 style={{
              fontSize: window.innerWidth <= 768 ? '1.75rem' : '2.25rem',
              fontWeight: '700',
              color: themeText,
              margin: 0
            }}>
              Edit Member
            </h1>
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Profile Image Upload */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', width: '100%' }}>
              <label style={{ cursor: 'pointer', position: 'relative', display: 'flex', justifyContent: 'center' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <div style={{
                  width: window.innerWidth <= 480 ? '8rem' : (window.innerWidth <= 768 ? '9rem' : '10rem'),
                  height: window.innerWidth <= 480 ? '8rem' : (window.innerWidth <= 768 ? '9rem' : '10rem'),
                  borderRadius: '50%',
                  background: imagePreview 
                    ? 'transparent'
                    : isDarkMode 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: imagePreview 
                    ? `4px solid ${themeBorder}`
                    : `3px dashed ${isDarkMode ? 'rgba(102, 126, 234, 0.5)' : '#667eea'}`,
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  boxShadow: imagePreview 
                    ? '0 8px 32px rgba(102, 126, 234, 0.3)'
                    : '0 4px 20px rgba(102, 126, 234, 0.15)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.transform = 'scale(1.08)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = imagePreview ? themeBorder : (isDarkMode ? 'rgba(102, 126, 234, 0.5)' : '#667eea');
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = imagePreview 
                    ? '0 8px 32px rgba(102, 126, 234, 0.3)'
                    : '0 4px 20px rgba(102, 126, 234, 0.15)';
                }}
                >
                  {imagePreview ? (
                    <>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          borderRadius: '50%'
                        }} 
                      />
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(102, 126, 234, 0.8)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'none'
                      }}
                      className="image-overlay"
                      >
                        <i className="fas fa-camera" style={{ fontSize: '2rem', color: 'white' }}></i>
                      </div>
                    </>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : '#667eea',
                      zIndex: 1,
                      position: 'relative'
                    }}>
                      <div style={{
                        width: '4rem',
                        height: '4rem',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                      }}>
                        <i className="fas fa-camera" style={{ fontSize: '1.75rem', color: 'white' }}></i>
                      </div>
                      <p style={{ 
                        fontSize: '1rem', 
                        margin: 0,
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}>
                        Change Photo
                      </p>
                    </div>
                  )}
                </div>
                <style>{`
                  label:hover .image-overlay {
                    opacity: 1 !important;
                  }
                  input::placeholder {
                    color: ${isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'};
                    transition: opacity 0.3s ease;
                  }
                  input:focus::placeholder {
                    opacity: 0.6;
                  }
                `}</style>
              </label>
            </div>
            {/* Name */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: themeText,
                marginBottom: '0.5rem',
                fontWeight: '500',
                fontSize: '0.9375rem'
              }}>
                Full Name *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: formData.name 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                      : themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${themeInputBorder}`,
                    borderRadius: '0.75rem',
                    color: themeText,
                    fontSize: '0.9375rem',
                    outline: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    fontWeight: formData.name ? '500' : '400'
                  }}
                  placeholder="Enter full name"
                  required
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeInputBorder;
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.background = formData.name 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                      : themeInputBg;
                  }}
                />
                {formData.name && (
                  <i className="fas fa-check-circle" style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#22c55e',
                    fontSize: '1rem',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}></i>
                )}
              </div>
            </div>
            {/* Email */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: themeText,
                marginBottom: '0.5rem',
                fontWeight: '500',
                fontSize: '0.9375rem'
              }}>
                Email Address *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: formData.email 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                      : themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${themeInputBorder}`,
                    borderRadius: '0.75rem',
                    color: themeText,
                    fontSize: '0.9375rem',
                    outline: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    fontWeight: formData.email ? '500' : '400'
                  }}
                  placeholder="e.g., student@example.com"
                  required
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeInputBorder;
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.background = formData.email 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                      : themeInputBg;
                  }}
                />
                {formData.email && (
                  <i className="fas fa-check-circle" style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#22c55e',
                    fontSize: '1rem',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}></i>
                )}
              </div>
            </div>
            {/* Roll Number */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: themeText,
                marginBottom: '0.5rem',
                fontWeight: '500',
                fontSize: '0.9375rem'
              }}>
                Roll Number *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={formData.rollNumber} 
                  onChange={(e) => setFormData({ ...formData, rollNumber: e.target.value.toUpperCase() })}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: formData.rollNumber 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                      : themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${themeInputBorder}`,
                    borderRadius: '0.75rem',
                    color: themeText,
                    fontSize: '0.9375rem',
                    outline: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    fontWeight: formData.rollNumber ? '600' : '400',
                    letterSpacing: formData.rollNumber ? '0.5px' : 'normal'
                  }}
                  placeholder="e.g..16900124169"
                  required
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeInputBorder;
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.background = formData.rollNumber 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)')
                      : themeInputBg;
                  }}
                />
                {formData.rollNumber && (
                  <i className="fas fa-check-circle" style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#22c55e',
                    fontSize: '1rem',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}></i>
                )}
              </div>
            </div>
            {/* Class Dropdown */}
            <div data-class-dropdown style={{ position: 'relative' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: themeText,
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  fontSize: '0.9375rem'
                }}>
                  Class *
                </label>
                <div
                  onClick={() => {
                    setClassDropdownOpen(!classDropdownOpen);
                    setYearDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${classDropdownOpen ? '#667eea' : themeInputBorder}`,
                    borderRadius: '0.75rem',
                    fontSize: '0.9375rem',
                    color: formData.class ? themeText : (isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s ease',
                    boxShadow: classDropdownOpen ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    fontWeight: formData.class ? '500' : '400'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {formData.class ? (
                      <>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.875rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          flexShrink: 0
                        }}>
                          <i className={`fas ${classes.find(s => s.value === formData.class)?.icon || 'fa-book'}`}></i>
                        </div>
                        <span>{formData.class}</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-layer-group" style={{ color: '#667eea' }}></i>
                        <span>Select Class</span>
                      </>
                    )}
                  </span>
                  <i 
                    className={`fas fa-chevron-${classDropdownOpen ? 'up' : 'down'}`}
                    style={{ 
                      color: '#667eea',
                      transition: 'transform 0.3s ease',
                      fontSize: '0.875rem'
                    }}
                  ></i>
                </div>
                {classDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    background: isDarkMode ? 'rgba(26, 26, 46, 0.95)' : 'white',
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
                    borderRadius: '0.75rem',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{
                      padding: '0.5rem',
                      maxHeight: '280px',
                      overflowY: 'auto'
                    }}>
                      {classes.map((classItem) => {
                        const isSelected = formData.class === classItem.value;
                        return (
                          <div
                            key={classItem.value}
                            onClick={() => {
                              setFormData({ ...formData, class: classItem.value });
                              setClassDropdownOpen(false);
                            }}
                            style={{
                              padding: '0.875rem 1rem',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              background: isSelected 
                                ? (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)')
                                : 'transparent',
                              transition: 'all 0.2s ease',
                              marginBottom: '0.25rem'
                            }}
                            onMouseOver={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.05)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: classItem.value === 'AIML' 
                                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.875rem',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              flexShrink: 0
                            }}>
                              <i className={`fas ${classItem.icon}`}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '600',
                                color: themeText,
                                fontSize: '0.9375rem'
                              }}>
                                {classItem.label}
                              </div>
                            </div>
                            {isSelected && (
                              <i className="fas fa-check" style={{ color: '#667eea', fontSize: '0.875rem' }}></i>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>
            {/* Year Dropdown */}
            <div data-year-dropdown style={{ position: 'relative' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: themeText,
                marginBottom: '0.5rem',
                fontWeight: '500',
                fontSize: '0.9375rem'
              }}>
                Year *
              </label>
                <div
                  onClick={() => {
                    setYearDropdownOpen(!yearDropdownOpen);
                    setClassDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: themeInputBg,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${yearDropdownOpen ? '#667eea' : themeInputBorder}`,
                    borderRadius: '0.75rem',
                    fontSize: '0.9375rem',
                    color: formData.year ? themeText : (isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s ease',
                    boxShadow: yearDropdownOpen ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    fontWeight: formData.year ? '500' : '400'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {formData.year ? (
                      <>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${years.find(y => y.value === formData.year)?.color || '#667eea'} 0%, ${years.find(y => y.value === formData.year)?.color || '#764ba2'} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.875rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          flexShrink: 0
                        }}>
                          <i className="fas fa-graduation-cap"></i>
                        </div>
                        <span>{formData.year}</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-graduation-cap" style={{ color: '#667eea' }}></i>
                        <span>Select Year</span>
                      </>
                    )}
                  </span>
                  <i 
                    className={`fas fa-chevron-${yearDropdownOpen ? 'up' : 'down'}`}
                    style={{ 
                      color: '#667eea',
                      transition: 'transform 0.3s ease',
                      fontSize: '0.875rem'
                    }}
                  ></i>
                </div>
                {yearDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    background: isDarkMode ? 'rgba(26, 26, 46, 0.95)' : 'white',
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
                    borderRadius: '0.75rem',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{
                      padding: '0.5rem',
                      maxHeight: '280px',
                      overflowY: 'auto'
                    }}>
                      {years.map((year) => {
                        const isSelected = formData.year === year.value;
                        return (
                          <div
                            key={year.value}
                            onClick={() => {
                              setFormData({ ...formData, year: year.value });
                              setYearDropdownOpen(false);
                            }}
                            style={{
                              padding: '0.875rem 1rem',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              background: isSelected 
                                ? (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)')
                                : 'transparent',
                              transition: 'all 0.2s ease',
                              marginBottom: '0.25rem'
                            }}
                            onMouseOver={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.05)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${year.color} 0%, ${year.color}dd 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.875rem',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              flexShrink: 0
                            }}>
                              <i className={`fas ${year.icon}`}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '600',
                                color: themeText,
                                fontSize: '0.9375rem'
                              }}>
                                {year.label}
                              </div>
                            </div>
                            {isSelected && (
                              <i className="fas fa-check" style={{ color: '#667eea', fontSize: '0.875rem' }}></i>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>
            {/* Team Selection */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: themeText,
                marginBottom: '1rem',
                fontWeight: '500',
                fontSize: '0.9375rem'
              }}>
                <i className="fas fa-users" style={{ color: '#667eea', fontSize: '1rem' }}></i>
                Select Team *
              </label>
              <div 
                className="team-selection"
                style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'nowrap',
                padding: '1rem',
                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(102, 126, 234, 0.03)',
                borderRadius: '1rem',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.1)'}`,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
              >
                <style>{`
                  div::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
                {teams.map((team) => {
                  const teamColors = getTeamColor(team);
                  const isSelected = selectedTeam === team;
                  return (
                    <div 
                      key={team} 
                      style={{ 
                        textAlign: 'center', 
                        cursor: 'pointer',
                        flex: '0 0 auto',
                        minWidth: '4.5rem'
                      }}
                      onClick={() => setSelectedTeam(team)}
                    >
                      <div style={{
                        width: '4.5rem',
                        height: '4.5rem',
                        borderRadius: '50%',
                        background: isSelected 
                          ? `linear-gradient(135deg, ${teamColors.color} 0%, ${teamColors.color}dd 100%)`
                          : themeCardBg,
                        border: `3px solid ${isSelected ? teamColors.border : themeBorder}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                        boxShadow: isSelected 
                          ? `0 0 0 4px ${teamColors.border}40, 0 8px 24px ${teamColors.color}50`
                          : `0 2px 8px rgba(0, 0, 0, 0.1)`,
                        position: 'relative'
                      }}
                      onMouseOver={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'scale(1.15) translateY(-4px)';
                          e.currentTarget.style.background = `linear-gradient(135deg, ${teamColors.color} 0%, ${teamColors.color}dd 100%)`;
                          e.currentTarget.style.borderColor = teamColors.border;
                          e.currentTarget.style.boxShadow = `0 8px 20px ${teamColors.color}40`;
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.background = themeCardBg;
                          e.currentTarget.style.borderColor = themeBorder;
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }
                      }}
                      >
                        <i className={`fas ${
                          team === 'Management' ? 'fa-crown' :
                          team === 'Tech' ? 'fa-microchip' :
                          team === 'Design' ? 'fa-palette' :
                          team === 'PR-Content' ? 'fa-bullhorn' :
                          team === 'Media' ? 'fa-video' :
                          team === 'Support' ? 'fa-headset' :
                          'fa-users'
                        }`}
                          style={{ 
                            fontSize: '1.75rem', 
                            color: isSelected ? 'white' : teamColors.color,
                            position: 'relative',
                            zIndex: 1,
                            transition: 'all 0.3s ease'
                          }}
                        ></i>
                      </div>
                      <p style={{ 
                        color: isSelected 
                          ? teamColors.color 
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'), 
                        fontSize: '0.8125rem', 
                        marginTop: '0.625rem', 
                        marginBottom: 0,
                        fontWeight: isSelected ? '600' : '500',
                        transition: 'all 0.3s ease',
                        whiteSpace: 'nowrap'
                      }}>
                        {team}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                fontSize: '1.125rem',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                opacity: isSubmitting ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseOver={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                }
              }}
              onMouseOut={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                }
              }}
            >
              {isSubmitting ? (
                <Loader variant="inline" size="sm" text="Updating Member..." isDarkMode={isDarkMode} />
              ) : (
                "Update Member"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Optional: Define the fadeInOut animation if you want it */}
      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
          10% { opacity: 1; transform: translateX(-50%) translateY(0); }
          90% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        }
      `}</style>
    </div>
  );
}